import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles, categories } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const [filteredArticles, setFilteredArticles] = useState(articles);

  useEffect(() => {
    if (slug) {
      setFilteredArticles(articles.filter((a) => a.categorySlug === slug));
    }
    window.scrollTo(0, 0);
  }, [slug]);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mb-4">
              <i className="ri-emotion-sad-line text-2xl"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-2">分类未找到</h1>
            <p className="text-foreground-500 mb-5">你访问的分类不存在，请检查链接</p>
            <Link to="/" className="inline-block px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryLabel = categories.find((c) => c.slug === slug)?.name || '分类';

  const webpageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${categoryLabel} - 51大赛吃瓜黑料网`,
    description: `${category?.description || ''} - 51大赛吃瓜黑料网${categoryLabel}分类下的全部爆料内容`,
    url: 'http://ahljjx.cn/',
    isPartOf: {
      '@type': 'WebSite',
      name: '51大赛吃瓜黑料网',
      url: 'http://ahljjx.cn/',
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首页',
          item: 'http://ahljjx.cn/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: categoryLabel,
          item: 'http://ahljjx.cn/',
        },
      ],
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webpageJsonLd) }}
      />
      <Navbar />
      <main>
        <Breadcrumb items={[{ label: category.name }]} />

        <section className="relative w-full h-[320px] md:h-[400px] flex items-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20dramatic%20dark%20cinematic%20scene%20representing%20Chinese%20social%20media%20scandal%20and%20gossip%20theme%2C%20moody%20atmosphere%20with%20warm%20red%20and%20amber%20lighting%2C%20a%20smartphone%20screen%20glowing%20in%20the%20darkness%2C%20leaked%20information%20concept%2C%20editorial%20photojournalism%20style%20with%20deep%20shadows%20and%20dramatic%20highlights&width=1600&height=900&seq=cat-hero-generic&orientation=landscape"
            alt={category.name}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

          <div className="relative z-10 w-full max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex flex-col items-center text-center">
              <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-2xl bg-primary-500/90 backdrop-blur-sm text-white mb-4 md:mb-5">
                <i className={`${category.icon} text-2xl md:text-3xl`}></i>
              </div>
              <h1 className="font-heading text-3xl md:text-5xl font-black text-white mb-3 md:mb-4 leading-tight">
                {category.name}
              </h1>
              <p className="text-white/80 text-sm md:text-lg max-w-xl leading-relaxed">
                {category.description}
              </p>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-10 md:mb-14">
              <div>
                <h2 className="font-heading text-3xl md:text-5xl font-black text-foreground-950 leading-tight">
                  全部爆料
                </h2>
                <p className="font-heading text-2xl md:text-3xl text-foreground-400 italic mt-1">
                  {filteredArticles.length} 篇相关文章
                </p>
              </div>
              <p className="text-foreground-500 text-sm max-w-md leading-relaxed">
                以下为{category.name}分类下的全部爆料内容，按发布时间倒序排列，最新大瓜排在最前。
              </p>
            </div>

            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
                {filteredArticles.map((article, idx) => (
                  <ArticleCard
                    key={article.id}
                    article={article}
                    variant={idx === 1 ? 'dark-icon' : 'default'}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-foreground-400 mb-4">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂无内容，敬请期待</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}